import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Chart } from "primereact/chart";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { SelectButton } from "primereact/selectbutton";

import KpiTile from "./KpiTile";
import {
  EMPTY_MESSAGES,
  filterByStars,
  isLongComment,
  STAR_FILTER_OPTIONS,
  StarFilter,
} from "./satisfactionComments";
import {
  OnboardingSatisfaction,
  SatisfactionComment,
  STAR_VALUES,
} from "../../../../types/onboardingAnalytics";
import { COCHARGE_DARK_GREEN } from "../../../../styles/theme_constants";
import { formatCount, formatPercent } from "../../../../utils/analyticsFormat";
import { formatInstant } from "../../../../utils/instantFormat";

interface SatisfactionSectionProps {
  satisfaction: OnboardingSatisfaction;
}

const NO_DATA = "No data yet";
const COMMENTS_PER_PAGE = 10;

/** A comment cut to two lines, with "Show more" when it is long enough to be cut. */
const CommentText: React.FC<{ text: string }> = ({ text }) => {
  const [expanded, setExpanded] = useState(false);
  const long = isLongComment(text);
  return (
    <div>
      <div
        className={`satisfaction-comment${
          long && !expanded ? " satisfaction-comment--collapsed" : ""
        }`}
      >
        {text}
      </div>
      {long && (
        <button
          type="button"
          className="p-link text-primary text-sm mt-1"
          onClick={(e) => {
            // The row opens the prospect; the toggle must not.
            e.stopPropagation();
            setExpanded((v) => !v);
          }}
        >
          {expanded ? "Show less" : "Show more"}
        </button>
      )}
    </div>
  );
};

/**
 * How customers rated their onboarding (CWA-207), for the prospects completed in
 * the selected months. Every figure covers that one cohort, so the response rate
 * is "rated of completed" and the average is taken over the same set.
 *
 * The caller shows its empty state when nothing was rated, so the null average
 * and rate are only guarded here, never expected.
 */
const SatisfactionSection: React.FC<SatisfactionSectionProps> = ({
  satisfaction,
}) => {
  const navigate = useNavigate();
  const [starFilter, setStarFilter] = useState<StarFilter>("all");
  const comments = useMemo(
    () => filterByStars(satisfaction.comments, starFilter),
    [satisfaction.comments, starFilter]
  );
  const average =
    satisfaction.averageStars != null
      ? satisfaction.averageStars.toFixed(1)
      : null;
  const responseRate = formatPercent(satisfaction.responseRatePercent);

  const chartData = {
    labels: STAR_VALUES.map((star) => (star === 1 ? "1 star" : `${star} stars`)),
    datasets: [
      {
        label: "Ratings",
        data: STAR_VALUES.map((star) => satisfaction.distribution[star]),
        backgroundColor: COCHARGE_DARK_GREEN,
        barThickness: 24,
      },
    ],
  };

  const chartOptions = {
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false } },
      y: { beginAtZero: true, ticks: { precision: 0 } },
    },
  };

  const starsBody = (c: SatisfactionComment) => `${c.stars} / 5`;
  const submittedBody = (c: SatisfactionComment) => formatInstant(c.submittedAt);
  const commentBody = (c: SatisfactionComment) => <CommentText text={c.comment} />;

  return (
    <div className="grid">
      <div className="col-12 md:col-6 lg:col-3">
        <KpiTile
          title="Average rating"
          icon="pi-star"
          value={average ?? NO_DATA}
          suffix="/ 5"
          muted={!average}
          hint={`From ${formatCount(satisfaction.ratedCount)} ratings`}
        />
      </div>
      <div className="col-12 md:col-6 lg:col-3">
        <KpiTile
          title="Response rate"
          icon="pi-percentage"
          value={responseRate ?? NO_DATA}
          muted={!responseRate}
          hint={`${formatCount(satisfaction.ratedCount)} rated of ${formatCount(
            satisfaction.completedCount
          )} completed`}
        />
      </div>
      <div className="col-12 lg:col-6">
        <div style={{ height: "12rem" }}>
          <Chart type="bar" data={chartData} options={chartOptions} />
        </div>
      </div>
      <div className="col-12 flex-column">
        <SelectButton
          className="mb-2"
          value={starFilter}
          options={STAR_FILTER_OPTIONS}
          onChange={(e) => e.value && setStarFilter(e.value)}
          allowEmpty={false}
          aria-label="Filter comments by stars"
        />
        <DataTable
          // Remount on a filter change so paging starts over at page 1.
          key={starFilter}
          value={comments}
          paginator={comments.length > COMMENTS_PER_PAGE}
          rows={COMMENTS_PER_PAGE}
          dataKey="prospectId"
          selectionMode="single"
          onSelectionChange={(e) =>
            navigate(
              `/onboarding/prospects/${(e.value as SatisfactionComment).prospectId}`
            )
          }
          className="p-datatable-sm cursor-pointer"
          emptyMessage={EMPTY_MESSAGES[starFilter]}
          aria-label="Comments"
        >
          <Column field="name" header="Prospect" style={{ width: "14rem" }} />
          <Column header="Stars" body={starsBody} style={{ width: "6rem" }} />
          <Column header="Comment" body={commentBody} />
          <Column header="Submitted" body={submittedBody} style={{ width: "14rem" }} />
        </DataTable>
      </div>
    </div>
  );
};

export default SatisfactionSection;
